import {sendFeedback} from '@api/feedback';
import Fade from '@components/Animation/Fade';
import ButtonFill from '@components/ButtonFill';
import FeedbackItem from '@components/FeedbackItem';
import type FeedbackItemType from '@interfaces/FeedbackItem';
import generateUid from '@services/uid';
import type {ChangeEvent, FormEvent, ReactNode} from 'react';
import {Component} from 'react';
import I18n from '@components/I18n';

interface Props {
    title: string;
    subtitle?: string;
    fields: FeedbackItemType[];
}

interface State {
    isSubmitted: boolean;
    items: FeedbackItemsList;
}

type FeedbackItemsList = Record<string, FeedbackItemType>;

export default class Feedback extends Component<Props, State> {
    public readonly state: State = {
        isSubmitted: false,
        items: this.getFeedbackItemsList(),
    };

    private getFeedbackItemsList(): FeedbackItemsList {
        const items: FeedbackItemsList = {};
        // eslint-disable-next-line array-callback-return
        this.props.fields.map(item => {
            items[generateUid()] = item;
        });
        return items;
    }

    private renderItems(): ReactNode[] {
        const result = [];
        for (const key of Object.keys(this.state.items)) {
            result.push(
                <FeedbackItem
                    key={key}
                    name={key}
                    {...this.state.items[key]}
                    onChange={this.handleItemChange}
                />,
            );
        }
        return result;
    }

    private validate(): boolean {
        let isValid = true;
        const {items} = this.state;

        for (const key in items) {
            if (!Object.hasOwn(items, key)) {
                continue;
            }

            if (items[key].isRequired && !items[key].value) {
                items[key].isError = true;
                isValid = false;
            }
        }

        this.setState({items});
        return isValid;
    }

    private readonly handleItemChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        const {items} = this.state;
        items[event.target.name].value = event.target.value;
        items[event.target.name].isError = false;
        this.setState({items});
    };

    private readonly handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        if (this.validate()) {
            void sendFeedback(
                this.props.title,
                location.href,
                Object.values(this.state.items).map(item => ({
                    title: item.title,
                    value: item.value,
                })),
            );
            this.setState({isSubmitted: true});
        }
    };

    public render(): ReactNode {
        return (
            <div className="feedback">
                <h3>{this.props.title}</h3>
                {this.props.subtitle ? (
                    <div className="feedback__subtitle">{this.props.subtitle}</div>
                ) : null}
                <form className="feedback__body" onSubmit={this.handleSubmit}>
                    {this.renderItems()}
                    <div className="feedback__controls">
                        <ButtonFill type="submit">
                            <I18n code="Feedback_sendButtonLabel" />
                        </ButtonFill>
                        <Fade in={this.state.isSubmitted} unmountOnExit>
                            <div className="feedback__success">
                                <I18n code="Feedback_successMessage" />
                            </div>
                        </Fade>
                    </div>
                </form>
            </div>
        );
    }
}
